import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import type {
  MarketingItemPayload,
  MarketingCampaignData as MarketingCampaign,
  MarketingObjectiveData as MarketingObjective,
  MarketingTaskData as MarketingTask,
} from '../../services/api';
import {
  RefreshCw, Loader2, Megaphone, ChevronDown, ChevronRight,
  Plus, Trash2, ExternalLink, Calendar, Target, CheckCircle2,
  Circle, Clock, Ban, ArrowRightLeft, ArrowDown, AlertTriangle,
  Save, X, Layers, ListChecks, Zap, Upload,
} from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const STATUS_OPTIONS = ['pending', 'in_progress', 'complete', 'blocked', 'cancelled'];
const PRIORITY_OPTIONS = ['high', 'medium', 'low'];
const EXECUTION_TYPES = ['parallel', 'sequential'];

const STATUS_CONFIG: Record<string, { icon: typeof Circle; color: string; bg: string; label: string }> = {
  pending: { icon: Circle, color: 'text-gray-500', bg: 'bg-gray-100 text-gray-700', label: 'Pending' },
  in_progress: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100 text-blue-700', label: 'In Progress' },
  complete: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-100 text-green-700', label: 'Complete' },
  blocked: { icon: Ban, color: 'text-red-500', bg: 'bg-red-100 text-red-700', label: 'Blocked' },
  cancelled: { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-100 text-yellow-700', label: 'Cancelled' },
};

const PRIORITY_CONFIG: Record<string, { color: string; bg: string }> = {
  high: { color: 'text-red-600', bg: 'bg-red-50 text-red-700 border-red-200' },
  medium: { color: 'text-yellow-600', bg: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  low: { color: 'text-green-600', bg: 'bg-green-50 text-green-700 border-green-200' },
};

// ─── Status Badge ─────────────────────────────────────────
function StatusBadge({ status, onChange }: { status: string; onChange?: (s: string) => void }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  if (!onChange) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  }

  return (
    <select
      value={status}
      onChange={(e) => onChange(e.target.value)}
      className={`text-xs font-medium rounded-full px-2 py-0.5 border cursor-pointer ${config.bg}`}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s} value={s}>
          {STATUS_CONFIG[s]?.label || s}
        </option>
      ))}
    </select>
  );
}

// ─── Priority Badge ───────────────────────────────────────
function PriorityBadge({ priority }: { priority: string }) {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${config.bg}`}>
      {priority}
    </span>
  );
}

// ─── Execution Type Indicator ─────────────────────────────
function ExecutionIndicator({ type }: { type: string }) {
  if (type === 'sequential') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-orange-600" title="Must be done sequentially">
        <ArrowDown className="w-3 h-3" />
        Sequential
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-blue-600" title="Can be done in parallel">
      <ArrowRightLeft className="w-3 h-3" />
      Parallel
    </span>
  );
}

// ─── Progress Bar ─────────────────────────────────────────
function ProgressBar({ percent, total, completed }: { percent: number; total: number; completed: number }) {
  const barColor = percent === 100 ? 'bg-green-500' : percent > 50 ? 'bg-blue-500' : percent > 0 ? 'bg-yellow-500' : 'bg-gray-300';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[80px]">
        <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${percent}%` }} />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">
        {completed}/{total} ({percent}%)
      </span>
    </div>
  );
}

// ─── Add Item Modal ───────────────────────────────────────
function AddItemModal({
  itemType,
  parentId,
  onSave,
  onCancel,
}: {
  itemType: 'campaign' | 'objective' | 'task';
  parentId: string;
  onSave: (item: MarketingItemPayload) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [executionType, setExecutionType] = useState('parallel');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    await onSave({
      item_type: itemType,
      parent_id: parentId,
      title: title.trim(),
      description: description.trim(),
      due_date: dueDate,
      priority,
      execution_type: executionType,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Add {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={`Enter ${itemType} title...`}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe what needs to be done..."
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Execution</label>
              <select
                value={executionType}
                onChange={(e) => setExecutionType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {EXECUTION_TYPES.map((t) => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline Edit Field ────────────────────────────────────
function InlineEdit({
  value,
  onSave,
  multiline = false,
  className = '',
}: {
  value: string;
  onSave: (val: string) => void;
  multiline?: boolean;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  if (!editing) {
    return (
      <span
        className={`cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1 ${className}`}
        onClick={() => { setEditValue(value); setEditing(true); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditValue(value); setEditing(true); } }}
        role="button"
        tabIndex={0}
        title="Click to edit"
      >
        {value || <span className="text-gray-500 italic">Click to edit</span>}
      </span>
    );
  }

  const save = () => {
    onSave(editValue);
    setEditing(false);
  };

  if (multiline) {
    return (
      <div className="flex flex-col gap-1">
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          rows={3}
          className="w-full px-2 py-1 border border-primary-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
        />
        <div className="flex gap-1">
          <button onClick={save} className="p-1 text-green-600 hover:text-green-700" title="Save">
            <Save className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setEditing(false)} className="p-1 text-gray-500 hover:text-gray-600" title="Cancel">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        className="flex-1 px-2 py-0.5 border border-primary-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <button onClick={save} className="p-1 text-green-600 hover:text-green-700">
        <Save className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => setEditing(false)} className="p-1 text-gray-500 hover:text-gray-600">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Task Row ─────────────────────────────────────────────
function TaskRow({
  task,
  onStatusChange,
  onUpdate,
  onDelete,
}: {
  task: MarketingTask;
  onStatusChange: (id: string, status: string) => void;
  onUpdate: (id: string, field: string, value: string) => void;
  onDelete: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-100 rounded-lg bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start gap-3 p-3">
        {/* Expand toggle */}
        <button onClick={() => setExpanded(!expanded)} className="mt-0.5 text-gray-500 hover:text-gray-600">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {/* Status */}
        <StatusBadge status={task.status} onChange={(s) => onStatusChange(task.item_id, s)} />

        {/* Title & meta */}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-800">
            <InlineEdit
              value={task.title}
              onSave={(val) => onUpdate(task.item_id, 'title', val)}
            />
          </div>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <PriorityBadge priority={task.priority} />
            <ExecutionIndicator type={task.execution_type} />
            {task.due_date && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3 h-3" />
                {task.due_date}
              </span>
            )}
          </div>
        </div>

        {/* Delete */}
        <button
          onClick={() => { if (confirm('Delete this task?')) onDelete(task.item_id); }}
          className="text-gray-300 hover:text-red-500 transition-colors"
          title="Delete task"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-10 pb-3 space-y-2 border-t border-gray-50">
          {task.description && (
            <div className="mt-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Description</span>
              <p className="text-sm text-gray-600 mt-0.5">
                <InlineEdit
                  value={task.description}
                  onSave={(val) => onUpdate(task.item_id, 'description', val)}
                  multiline
                  className="text-sm text-gray-600"
                />
              </p>
            </div>
          )}
          {task.notes && (
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Notes</span>
              <p className="text-sm text-gray-500 italic mt-0.5">{task.notes}</p>
            </div>
          )}
          {task.links && task.links.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase">Links</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {task.links.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 bg-primary-50 px-2 py-1 rounded"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Objective Section ────────────────────────────────────
function ObjectiveSection({
  objective,
  onStatusChange,
  onUpdate,
  onDelete,
  onAddTask,
}: {
  objective: MarketingObjective;
  onStatusChange: (id: string, status: string) => void;
  onUpdate: (id: string, field: string, value: string) => void;
  onDelete: (id: string) => void;
  onAddTask: (parentId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const tasks = objective.tasks || [];
  const completedTasks = tasks.filter((t) => t.status === 'complete').length;
  const totalTasks = tasks.length;
  const percent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50/50">
      {/* Objective header */}
      <div
        className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <div className="mt-0.5">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </div>

        <Layers className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />

        <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-800">
              <InlineEdit
                value={objective.title}
                onSave={(val) => onUpdate(objective.item_id, 'title', val)}
                className="font-semibold text-gray-800"
              />
            </span>
            <StatusBadge status={objective.status} onChange={(s) => onStatusChange(objective.item_id, s)} />
            <ExecutionIndicator type={objective.execution_type} />
          </div>
          <div className="flex items-center gap-4 mt-1">
            {objective.due_date && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3 h-3" />
                Due: {objective.due_date}
              </span>
            )}
            <ProgressBar percent={percent} total={totalTasks} completed={completedTasks} />
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); if (confirm('Delete this objective and all its tasks?')) onDelete(objective.item_id); }}
          className="text-gray-300 hover:text-red-500 transition-colors"
          title="Delete objective"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4">
          {objective.description && (
            <p className="text-sm text-gray-600 mb-3 pl-8">{objective.description}</p>
          )}

          {/* Tasks */}
          <div className="space-y-2 pl-8">
            {tasks.map((task) => (
              <TaskRow
                key={task.item_id}
                task={task}
                onStatusChange={onStatusChange}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </div>

          {/* Add task button */}
          <button
            onClick={() => onAddTask(objective.item_id)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600 mt-3 pl-8"
          >
            <Plus className="w-4 h-4" />
            Add Task
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Campaign Card ────────────────────────────────────────
function CampaignCard({
  campaign,
  onStatusChange,
  onUpdate,
  onDelete,
  onAddObjective,
  onAddTask,
}: {
  campaign: MarketingCampaign;
  onStatusChange: (id: string, status: string) => void;
  onUpdate: (id: string, field: string, value: string) => void;
  onDelete: (id: string) => void;
  onAddObjective: (parentId: string) => void;
  onAddTask: (parentId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Campaign header */}
      <div
        className="flex items-start gap-4 p-5 cursor-pointer hover:bg-gray-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
      >
        <div className="mt-1">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </div>

        <Target className="w-6 h-6 text-violet-500 mt-0.5 flex-shrink-0" />

        <div className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">
              <InlineEdit
                value={campaign.title}
                onSave={(val) => onUpdate(campaign.item_id, 'title', val)}
                className="text-lg font-bold text-gray-900"
              />
            </h2>
            <StatusBadge status={campaign.status} onChange={(s) => onStatusChange(campaign.item_id, s)} />
          </div>

          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {(campaign.start_date || campaign.end_date) && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="w-3.5 h-3.5" />
                {campaign.start_date} — {campaign.end_date}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <ListChecks className="w-3.5 h-3.5" />
              {campaign.objectives?.length || 0} objectives
            </span>
            <ProgressBar
              percent={campaign.percent_complete}
              total={campaign.total_tasks}
              completed={campaign.completed_tasks}
            />
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (confirm('Delete this entire campaign and all its objectives and tasks?')) onDelete(campaign.item_id);
          }}
          className="text-gray-300 hover:text-red-500 transition-colors"
          title="Delete campaign"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 p-5 pt-4 space-y-4">
          {/* Campaign description & metrics */}
          {campaign.description && (
            <p className="text-sm text-gray-600">{campaign.description}</p>
          )}
          {campaign.target_metrics && (
            <div className="bg-violet-50 rounded-lg p-3">
              <span className="text-xs font-semibold text-violet-600 uppercase">Target Metrics</span>
              <p className="text-sm text-violet-700 mt-1">{campaign.target_metrics}</p>
            </div>
          )}

          {/* Objectives */}
          <div className="space-y-3">
            {(campaign.objectives || []).map((obj) => (
              <ObjectiveSection
                key={obj.item_id}
                objective={obj}
                onStatusChange={onStatusChange}
                onUpdate={onUpdate}
                onDelete={onDelete}
                onAddTask={onAddTask}
              />
            ))}
          </div>

          {/* Add objective button */}
          <button
            onClick={() => onAddObjective(campaign.item_id)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-primary-600"
          >
            <Plus className="w-4 h-4" />
            Add Objective
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Summary Dashboard ────────────────────────────────────
function SummaryDashboard({ campaigns }: { campaigns: MarketingCampaign[] }) {
  const totalCampaigns = campaigns.length;
  const totalObjectives = campaigns.reduce((sum, c) => sum + (c.objectives?.length || 0), 0);
  const totalTasks = campaigns.reduce((sum, c) => sum + c.total_tasks, 0);
  const completedTasks = campaigns.reduce((sum, c) => sum + c.completed_tasks, 0);
  const overallPercent = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const statusCounts = { pending: 0, in_progress: 0, complete: 0, blocked: 0, cancelled: 0 };
  campaigns.forEach((c) =>
    c.objectives?.forEach((o) =>
      o.tasks?.forEach((t) => {
        if (t.status in statusCounts) statusCounts[t.status as keyof typeof statusCounts]++;
      })
    )
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Target className="w-4 h-4 text-violet-500" />
          <span className="text-xs text-gray-500">Campaigns</span>
        </div>
        <div className="text-2xl font-bold text-gray-800">{totalCampaigns}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Layers className="w-4 h-4 text-indigo-500" />
          <span className="text-xs text-gray-500">Objectives</span>
        </div>
        <div className="text-2xl font-bold text-gray-800">{totalObjectives}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-1">
          <ListChecks className="w-4 h-4 text-blue-500" />
          <span className="text-xs text-gray-500">Tasks</span>
        </div>
        <div className="text-2xl font-bold text-gray-800">{totalTasks}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle2 className="w-4 h-4 text-green-500" />
          <span className="text-xs text-gray-500">Completed</span>
        </div>
        <div className="text-2xl font-bold text-gray-800">{completedTasks}</div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-yellow-500" />
          <span className="text-xs text-gray-500">Progress</span>
        </div>
        <div className="text-2xl font-bold text-gray-800">{overallPercent}%</div>
        <div className="mt-1">
          <ProgressBar percent={overallPercent} total={totalTasks} completed={completedTasks} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Marketing Page ──────────────────────────────────
export function Marketing() {
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModal, setAddModal] = useState<{ type: 'campaign' | 'objective' | 'task'; parentId: string } | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getMarketingData();
      setCampaigns(result.campaigns || []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useAutoRefresh(loadData);

  const handleStatusChange = async (itemId: string, status: string) => {
    try {
      await api.updateMarketingItem(itemId, { status });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update status');
    }
  };

  const handleUpdate = async (itemId: string, field: string, value: string) => {
    try {
      await api.updateMarketingItem(itemId, { [field]: value });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update item');
    }
  };

  const handleDelete = async (itemId: string) => {
    try {
      await api.deleteMarketingItem(itemId);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to delete item');
    }
  };

  const handleCreate = async (item: MarketingItemPayload) => {
    try {
      await api.createMarketingItem(item);
      setAddModal(null);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to create item');
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will load the full marketing plan into the system. Continue?')) return;
    setSeeding(true);
    setError(null);
    try {
      // Fetch the marketing plan JSON and seed it
      const response = await fetch('/marketing-plan.json');
      if (!response.ok) {
        // If the static file doesn't exist, use the embedded plan
        setError('Marketing plan JSON file not found. Place marketing-plan.json in webapp/public/');
        setSeeding(false);
        return;
      }
      const plan = await response.json();
      await api.seedMarketingData({ campaigns: plan.campaigns });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(axiosErr?.response?.data?.message || axiosErr?.message || 'Failed to seed marketing data');
    } finally {
      setSeeding(false);
    }
  };

  // Filter campaigns based on status filter
  const filteredCampaigns = filterStatus === 'all'
    ? campaigns
    : campaigns.filter((c) => c.status === filterStatus);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Megaphone className="w-7 h-7 text-violet-500" />
          <h1 className="text-2xl font-bold text-gray-800">Marketing Plan</h1>
        </div>
        <div className="flex items-center gap-2">
          {campaigns.length === 0 && !loading && (
            <button
              onClick={handleSeedData}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Load Marketing Plan
            </button>
          )}
          <button
            onClick={() => setAddModal({ type: 'campaign', parentId: '' })}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Campaign
          </button>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600" aria-label="Dismiss error">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && campaigns.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-20">
          <Megaphone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">No Marketing Data Yet</h2>
          <p className="text-gray-500 mb-6">
            Click "Load Marketing Plan" to seed the comprehensive marketing plan, or create campaigns manually.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Dashboard */}
          <SummaryDashboard campaigns={campaigns} />

          {/* Status Filter */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-gray-500">Filter:</span>
            {['all', ...STATUS_OPTIONS].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  filterStatus === s
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>

          {/* Campaign List */}
          <div className="space-y-4">
            {filteredCampaigns.map((campaign) => (
              <CampaignCard
                key={campaign.item_id}
                campaign={campaign}
                onStatusChange={handleStatusChange}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onAddObjective={(parentId) => setAddModal({ type: 'objective', parentId })}
                onAddTask={(parentId) => setAddModal({ type: 'task', parentId })}
              />
            ))}
          </div>
        </>
      )}

      {/* Add Item Modal */}
      {addModal && (
        <AddItemModal
          itemType={addModal.type}
          parentId={addModal.parentId}
          onSave={handleCreate}
          onCancel={() => setAddModal(null)}
        />
      )}
    </div>
  );
}
